import {ArchiveIcon, GitPullRequestClosedIcon, GitPullRequestIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button} from '@primer/react'
import {useState} from 'react'

import type {RepoPullsBulkUpdate} from '../helpers/repo-pulls-bulk-update'

const BULK_STATE_OPTIONS = [
  {label: 'Open', update: {state: 'open'}, icon: GitPullRequestIcon, colorClass: 'fgColor-open'},
  {label: 'Closed', update: {state: 'closed'}, icon: GitPullRequestClosedIcon, colorClass: 'fgColor-closed'},
] as const

const BULK_ARCHIVE_OPTIONS = [
  {label: 'Archived', update: {archive: 'true'}, icon: ArchiveIcon},
  {label: 'Unarchived', update: {archive: 'false'}, icon: ArchiveIcon},
] as const

interface RepoPullsBulkMarkAsProps {
  canArchive: boolean
  canMarkAsOpen: boolean
  disabled: boolean
  nested: boolean
  onUpdate: (update: RepoPullsBulkUpdate) => void
  showMarkAsClosed: boolean
  showMarkAsOpen: boolean
}

export function RepoPullsBulkMarkAs({
  canArchive,
  canMarkAsOpen,
  disabled,
  nested,
  onUpdate,
  showMarkAsClosed,
  showMarkAsOpen,
}: RepoPullsBulkMarkAsProps) {
  const [isOpen, setIsOpen] = useState(false)

  const stateOptions = BULK_STATE_OPTIONS.filter(option =>
    option.update.state === 'open' ? showMarkAsOpen : showMarkAsClosed,
  )
  const options = canArchive ? [...stateOptions, ...BULK_ARCHIVE_OPTIONS] : stateOptions
  const items = options.map(option => {
    const isMarkAsOpenDisabled = 'state' in option.update && option.update.state === 'open' && !canMarkAsOpen
    const isDisabled = disabled || isMarkAsOpenDisabled

    return (
      <ActionList.Item
        key={option.label}
        aria-disabled={isDisabled || undefined}
        inactiveText={isMarkAsOpenDisabled ? 'Merged pull requests cannot be reopened' : undefined}
        onSelect={() => {
          if (isDisabled) return
          onUpdate(option.update)
          setIsOpen(false)
        }}
      >
        <ActionList.LeadingVisual>
          <option.icon className={'colorClass' in option ? option.colorClass : undefined} />
        </ActionList.LeadingVisual>
        {option.label}
      </ActionList.Item>
    )
  })

  if (nested) {
    return (
      <ActionMenu open={isOpen} onOpenChange={open => !disabled && setIsOpen(open)}>
        <ActionMenu.Anchor>
          <ActionList.Item aria-disabled={disabled || undefined}>
            <ActionList.LeadingVisual>
              <GitPullRequestClosedIcon />
            </ActionList.LeadingVisual>
            Mark as
          </ActionList.Item>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList>{items}</ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    )
  }

  return (
    <ActionMenu open={isOpen} onOpenChange={open => !disabled && setIsOpen(open)}>
      <ActionMenu.Anchor>
        <Button
          aria-disabled={disabled || undefined}
          leadingVisual={GitPullRequestClosedIcon}
          trailingVisual={TriangleDownIcon}
        >
          Mark as
        </Button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>{items}</ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
